import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { APPOINTMENT_STATUS_LABELS, APPOINTMENT_TYPE_LABELS } from "@clinic/types";
import { appointmentStatusTone } from "@clinic/ui";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { SkeletonRows } from "@/components/ui/skeleton";
import { Table, TBody, Td, Th, THead, Tr } from "@/components/ui/table";

export function PatientAppointmentsTab({ patientId }: { patientId: string }) {
  const appointments = useQuery({
    queryKey: ["patient-appointments", patientId],
    queryFn: () => api.patients.appointments(patientId),
  });

  if (appointments.isLoading) return <SkeletonRows rows={4} />;

  return (
    <Table>
      <THead>
        <Tr>
          <Th>Date</Th>
          <Th>Time</Th>
          <Th>Type</Th>
          <Th>Doctor</Th>
          <Th>Status</Th>
        </Tr>
      </THead>
      <TBody>
        {(appointments.data ?? []).map((a) => (
          <Tr key={a.id}>
            <Td>{format(new Date(a.startTime), "d MMM yyyy")}</Td>
            <Td className="font-mono text-xs">
              {format(new Date(a.startTime), "HH:mm")}–{format(new Date(a.endTime), "HH:mm")}
            </Td>
            <Td>{APPOINTMENT_TYPE_LABELS[a.type]}</Td>
            <Td>{a.doctor?.name}</Td>
            <Td>
              <Badge tone={appointmentStatusTone[a.status] ?? "neutral"}>
                {APPOINTMENT_STATUS_LABELS[a.status]}
              </Badge>
            </Td>
          </Tr>
        ))}
        {(appointments.data ?? []).length === 0 && (
          <Tr>
            <Td colSpan={5} className="py-10 text-center text-text-tertiary">
              No appointments for this patient.
            </Td>
          </Tr>
        )}
      </TBody>
    </Table>
  );
}
